import { hasPermission } from '@ctrl/db';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTable } from '@tanstack/react-table';
import { Gift } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  DataTable,
  DataTablePagination,
  dataTableColumnHelper,
  dataTableDefaultColumn,
  dataTableFeatures,
} from '@/components/data-table';
import { TextField } from '@/components/form-fields';
import { ReadOnlyNotice, SectionCard } from '@/components/settings/settings-kit';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import {
  type GiftCardRecord,
  generateGiftCardCode,
  issueGiftCard,
  listGiftCards,
  lookupGiftCard,
} from '@/lib/retention';

/**
 * Gift cards.
 *
 * A card's code is stored as a hash and nothing else, so this screen can
 * issue one (showing the code exactly once, at the moment it is created),
 * find one by a code somebody reads out, and adjust a balance -- but it can
 * never show an existing card's code again, because nothing anywhere can.
 */
export const Route = createFileRoute('/gift-cards/')({
  component: GiftCardsPage,
});

const usd = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

const column = dataTableColumnHelper<GiftCardRecord>();

const columns = column.columns([
  column.accessor('last4', {
    header: 'Card',
    cell: ({ row }) => (
      <div className="flex min-w-0 items-center gap-2">
        <Link
          to="/gift-cards/$cardId"
          params={{ cardId: row.original.id }}
          className="font-mono hover:underline"
        >
          •••• {row.original.last4 ?? '????'}
        </Link>

        {!row.original.active && <Badge variant="outline">Void</Badge>}
      </div>
    ),
  }),

  column.accessor('recipientEmail', {
    header: 'Recipient',
    cell: ({ getValue }) => (
      <span className="text-muted-foreground block truncate text-sm">{getValue() ?? '—'}</span>
    ),
  }),

  column.accessor('createdAt', {
    header: 'Issued',
    cell: ({ getValue }) => (
      <span className="text-muted-foreground text-sm">
        {new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(new Date(getValue()))}
      </span>
    ),
  }),

  column.accessor('initialBalanceCents', {
    header: 'Issued for',
    meta: { className: 'text-muted-foreground text-right tabular-nums' },
    cell: ({ getValue }) => usd(getValue()),
  }),

  column.accessor('balanceCents', {
    header: 'Balance',
    meta: { className: 'text-right tabular-nums' },
    cell: ({ getValue }) => usd(getValue()),
  }),
]);

function GiftCardsPage() {
  const { activeAccountId, claims } = useAuth();
  const accountId = activeAccountId ?? '';
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const canManage = accountId ? hasPermission(claims, accountId, 'discounts.manage') : false;

  const [amount, setAmount] = useState('50.00');
  const [recipient, setRecipient] = useState('');
  const [issued, setIssued] = useState<string | null>(null);
  const [lookupCode, setLookupCode] = useState('');
  const [found, setFound] = useState<{ balanceCents: number; last4: string } | null>(null);

  const cards = useQuery({
    queryKey: ['gift-cards', accountId],
    queryFn: () => listGiftCards(accountId),
    enabled: Boolean(accountId),
  });

  const rows = cards.data ?? [];

  const table = useTable({
    features: dataTableFeatures,
    defaultColumn: dataTableDefaultColumn,
    columns,
    data: rows,
    initialState: {
      pagination: { pageIndex: 0, pageSize: 25 },
      // Newest first: the card somebody just issued is the one they are
      // looking for.
      sorting: [{ id: 'createdAt', desc: true }],
    },
  });

  const issue = useMutation({
    mutationFn: async () => {
      const cents = Math.round(Number(amount) * 100);

      if (!Number.isFinite(cents) || cents <= 0) throw new Error('Enter an amount.');

      const code = generateGiftCardCode();

      await issueGiftCard(accountId, {
        code,
        amountCents: cents,
        recipientEmail: recipient || null,
      });

      return code;
    },
    onSuccess: (code) => {
      // Shown once. There is no second chance: only the hash is kept.
      setIssued(code);
      setRecipient('');
      queryClient.invalidateQueries({ queryKey: ['gift-cards', accountId] });
    },
    onError: (error: Error) => toast.error(error.message.replace(/^[a-z_]+:\s*/i, '')),
  });

  const lookup = useMutation({
    mutationFn: async () => {
      const card = (await lookupGiftCard(accountId, lookupCode.trim())) as {
        balance_cents?: number;
        last4?: string;
      } | null;

      if (!card) throw new Error('No card with that code.');

      return { balanceCents: card.balance_cents ?? 0, last4: card.last4 ?? '' };
    },
    onSuccess: setFound,
    onError: (error: Error) => {
      setFound(null);
      toast.error(error.message.replace(/^[a-z_]+:\s*/i, ''));
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Gift cards</h1>
        <p className="text-muted-foreground text-sm">
          {cards.isLoading ? 'Loading…' : `${rows.length} issued`}
        </p>
      </div>

      {!canManage && <ReadOnlyNotice permission="discounts.manage" />}

      <div className="grid gap-6 lg:grid-cols-2">
        <SectionCard
          title="Issue a card"
          description="The code is shown once, here, and stored only as a hash — write it down or send it before you close this."
        >
          <fieldset disabled={!canManage} className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField id="card-amount" label="Amount" value={amount} onChange={setAmount} />
              <TextField
                id="card-recipient"
                label="Recipient email"
                description="Optional. Delivery is a later phase; this records who it is for."
                value={recipient}
                onChange={setRecipient}
              />
            </div>

            <Button type="button" onClick={() => issue.mutate()} disabled={issue.isPending}>
              {issue.isPending ? 'Issuing…' : 'Issue card'}
            </Button>

            {issued && (
              <div className="rounded-lg border border-dashed p-4">
                <p className="text-sm font-medium">Give the customer this code</p>
                <code className="mt-2 block font-mono text-lg tracking-widest">{issued}</code>
                <p className="text-muted-foreground mt-2 text-xs">
                  It cannot be shown again — only its last four digits are recoverable.
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="mt-2"
                  onClick={() => setIssued(null)}
                >
                  Done
                </Button>
              </div>
            )}
          </fieldset>
        </SectionCard>

        <SectionCard
          title="Look one up"
          description="For a customer reading a code down the phone. Returns the balance, never the code."
        >
          <div className="flex items-end gap-3">
            <TextField
              id="card-lookup"
              label="Code"
              className="flex-1"
              value={lookupCode}
              onChange={setLookupCode}
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => lookup.mutate()}
              disabled={!lookupCode.trim() || lookup.isPending}
            >
              Check
            </Button>
          </div>

          {found && (
            <p className="text-sm">
              Card ending <span className="font-mono">{found.last4}</span> has{' '}
              <span className="font-medium">{usd(found.balanceCents)}</span> on it.
            </p>
          )}
        </SectionCard>
      </div>

      <DataTable
        table={table}
        columnCount={columns.length}
        isLoading={cards.isLoading}
        onRowActivate={(row) => navigate({ to: '/gift-cards/$cardId', params: { cardId: row.id } })}
        emptyMessage={
          <span className="flex flex-col items-center gap-2">
            <Gift className="size-6" />
            No cards issued yet.
          </span>
        }
      />

      <DataTablePagination table={table} total={rows.length} />
    </div>
  );
}
